import { computed } from 'vue';
import { useStore } from 'vuex';
import Constants from '@/utils/constants';

/**
 * Conference call composable that provides access to conference call-related getters
 */
export function useConferenceCall() {
  const store = useStore();

  // Conference call module getters
  const isSGM = computed(() => store.getters['conferenceCall/GET_IS_SMALL_GROUP_MEETING']);
  const isSGMType = computed(() => store.getters['conferenceCall/GET_IS_SMALL_GROUP_MEETING_TYPE']);
  const isSGMOperatorAssisted = computed(() => store.getters['conferenceCall/GET_IS_SMALL_GROUP_MEETING_OPERATOR_ASSISTED']);
  const isSGMWebinarOperatorAssisted = computed(() => store.getters['conferenceCall/GET_IS_SMALL_GROUP_MEETING_WEBINAR_OPERATOR_ASSISTED']);
  const conferenceCallDetails = computed(() => store.getters['conferenceCall/GET_CONFERENCE_CALL_DETAILS']);
  const conferenceCallStatus = computed(() => store.getters['conferenceCall/GET_CONFERENCE_CALL_STATUS']);
  const uniqueId = computed(() => store.getters['conferenceCall/GET_UNIQUE_ID']);
  const releaseInfo = computed(() => store.getters['conferenceCall/FETCH_RELEASE_INFO']);
  const utcTimeString = computed(() => store.getters['conferenceCall/FETCH_UTC_TIME_STRING']);
  
  // Additional getters for CallConfirmation component
  const dateTimeNow = computed(() => store.getters['conferenceCall/GET_TIME_DETAILS']);
  const callStatus = computed(() => store.getters['conferenceCall/GET_CONFERENCE_CALL_PAGE_STATUS']);
  const hasConferenceCallStarted = computed(() => store.getters['conferenceCall/GET_HAS_CONFERENCE_STARTED']);
  const participants = computed(() => store.getters['conferenceCall/GET_REGISTERED_USER_FOR_CALL']);
  const userStatus = computed(() => store.getters['conferenceCall/GET_USER_STATUS']);
  const isParticipantAccessRevoked = computed(() => store.getters['conferenceCall/GET_PARTICIPANT_ACCESS_REVOKED']);
  
  // Additional getters for Register and SupplementalRegister components
  const isSlideLiveVideo = computed(() => store.getters['conferenceCall/GET_IS_SLIDE_LIVE_VIDEO']);
  const isSlideConf = computed(() => store.getters['conferenceCall/GET_IS_SLIDE_CONF_SHOW']);
  const isVideoWebinar = computed(() => store.getters['conferenceCall/GET_IS_VIDEO_WEBINAR']);
  const isConfOnly = computed(() => store.getters['conferenceCall/GET_IS_CONF_ONLY_SHOW']);
  const showId = computed(() => store.getters['conferenceCall/GET_SHOW_ID']);
  const confId = computed(() => store.getters['conferenceCall/GET_CONF_ID']);
  const isListenOnly = computed(() => store.getters['conferenceCall/GET_IS_LISTEN_ONLY']);
  const isRegistered = computed(() => store.getters['conferenceCall/GET_REGISTERED_USER_FOR_CALL']);
  const isPublicParticipantValue = computed(() => store.getters['conferenceCall/GET_PUBLIC_PARTICIPANT']);

  // Actions
  const registerForConferenceCall = () => store.dispatch('conferenceCall/registerForConferenceCall');
  const updateRegisteredParticipantForConferenceCall = () => store.dispatch('conferenceCall/updateRegisteredParticipantForConferenceCall');
  const getRegisteredUserForCall = () => store.dispatch('conferenceCall/getRegisteredUserForCall');
  
  // Additional actions for CallConfirmation component
  const fetchDateTimeNow = (param) => store.dispatch('conferenceCall/fetchDateTimeNow', param);
  const fetchConferenceCallStatus = () => store.dispatch('conferenceCall/fetchConferenceCallStatus');
  const stopUserStatusPolling = () => store.dispatch('conferenceCall/stopUserStatusPolling');
  const stopConferenceCallStatusPolling = () => store.dispatch('conferenceCall/stopConferenceCallStatusPolling');
  const startParticipantPolling = () => store.dispatch('conferenceCall/startParticipantPolling');
  const stopParticipantPolling = () => store.dispatch('conferenceCall/stopParticipantPolling');
  const resetConferenceStatus = () => store.dispatch('conferenceCall/resetConferenceStatus');
  const updateMetadata = (data) => store.dispatch('conferenceCall/updateMetadata', data);

  // Computed properties for replay functionality
  const isReplayEnable = computed(() => {
    return (
      conferenceCallDetails.value.ReplayType == Constants.ReplayTypes.WebOnly ||
      conferenceCallDetails.value.ReplayType == Constants.ReplayTypes.PhoneOnly ||
      conferenceCallDetails.value.ReplayType == Constants.ReplayTypes.WebAndPhone ||
      conferenceCallDetails.value.ReplayType == Constants.ReplayTypes.NrsLiveVideoAndSlides ||
      conferenceCallDetails.value.ReplayType == Constants.ReplayTypes.NrsLiveAudioOnly     
    );
  });

  // Conference status checks
  const isEndedStatus = computed(() => {
    return (
      conferenceCallDetails.value.Status === Constants.ConferenceStatus.Ended ||
      conferenceCallStatus.value.Status === Constants.ConferenceStatus.Ended
    );
  });

  const isExpiredStatus = computed(() => {
    return (
      conferenceCallDetails.value.Status === Constants.ConferenceStatus.Expired ||
      conferenceCallStatus.value.Status === Constants.ConferenceStatus.Expired
    );
  });

  return {
    // Base getters
    isSGM,
    isSGMType,
    isSGMOperatorAssisted,
    isSGMWebinarOperatorAssisted,
    conferenceCallDetails,
    conferenceCallStatus,
    uniqueId,
    releaseInfo,
    utcTimeString,
    dateTimeNow,
    callStatus,
    hasConferenceCallStarted,
    participants,
    userStatus,
    isParticipantAccessRevoked,
    isSlideLiveVideo,
    isSlideConf,
    isVideoWebinar,
    isConfOnly,
    showId,
    confId,
    isListenOnly,
    isRegistered,
    isPublicParticipantValue,
    
    // Actions
    registerForConferenceCall,
    updateRegisteredParticipantForConferenceCall,
    getRegisteredUserForCall,
    fetchDateTimeNow,
    fetchConferenceCallStatus,
    stopUserStatusPolling,
    stopConferenceCallStatusPolling,
    startParticipantPolling,
    stopParticipantPolling,
    resetConferenceStatus,
    updateMetadata,
    
    // Computed properties
    isReplayEnable,
    isEndedStatus,
    isExpiredStatus,
  };
}
